<template>
    <section class="py-16 lg:py-[150px] px-5 lg:px-[120px]" :class="backgroundColor">
        <div class="mx-auto text-center">
            <!-- Block 1: Main Title + Intro -->
            <h2 class="text-[28px] md:text-4xl lg:text-[44px] lg:leading-[57px] font-semibold" :style="{ color: titleColor }">
                <span v-html="renderTitle()"></span>
            </h2>

            <p class="max-w-6xl mx-auto mt-[50px] text-[18px] leading-[130%] lg:leading-relaxed font-pro" :style="{ color: introTextColor }" v-html="introText"></p>

            <!-- Block 2: Subtitle -->
            <h3 class="lg:text-[32px] text-[26px] lg:leading-[42px] font-semibold mt-[50px]" :style="{ color: titleColor }">
                <span v-html="renderSubtitle()"></span>
            </h3>

            <!-- Block 3: 2x2 Card Grid -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-5 mt-[50px] mx-auto">
                <div
                    v-for="(card, index) in cards"
                    :key="index"
                    class="border rounded-2xl shadow-sm p-[30px] text-left"
                    :class="cardBackground"
                    :style="{ borderColor: cardBorderColor }"
                >
                    <!-- Icon -->
                    <img
                        v-if="card.icon && (card.icon.startsWith('/') || card.icon.startsWith('http'))"
                        :src="$asset(card.icon, 'public')"
                        class="w-[70px] h-[70px] block mx-auto"
                        :alt="card.title"
                    />
                    <i v-else class="text-4xl text-center mx-auto w-full block" :class="[card.icon, iconColor]"></i>

                    <!-- Title -->
                    <h4
                        class="font-semibold text-center leading-tight lg:leading-normal text-[22px] lg:text-[24px] mt-4 hyphens-auto wrap-break-word"
                        :style="{ color: cardTitleColor }"
                        v-html="card.title"
                    ></h4>

                    <!-- Description -->
                    <p class="text-[18px] text-center leading-[130%] lg:leading-relaxed mt-3 font-pro" :style="{ color: cardTextColor }" v-html="card.description"></p>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup>
const props = defineProps({
    backgroundColor: { type: String, default: '#ffffff' },
    title: { type: String, required: true },
    titleHighlight: { type: String, default: '' },
    titleHighlightColor: { type: String, default: '#0d9488' },
    titleHighlightGradientFrom: { type: String, default: '' },
    titleHighlightGradientTo: { type: String, default: '' },
    titleColor: { type: String, default: '#1a1a1a' },
    introText: { type: String, default: '' },
    introTextColor: { type: String, default: '#4b5563' },
    subtitle: { type: String, default: '' },
    subtitleHighlight: { type: String, default: '' },
    cards: { type: Array, required: true },
    cardBorderColor: { type: String, default: '#f3f4f6' },
    cardTitleColor: { type: String, default: '#1a1a1a' },
    cardTextColor: { type: String, default: '#4b5563' },
    iconColor: { type: String, default: 'text-teal-600' },
    cardBackground: { type: String, default: 'bg-white' }
});

const getHighlightStyle = () => {
    if (props.titleHighlightGradientFrom && props.titleHighlightGradientTo) {
        return `background: linear-gradient(to top, ${props.titleHighlightGradientFrom}, ${props.titleHighlightGradientTo}); -webkit-background-clip: text; -webkit-text-fill-color: transparent;`;
    }

    return `color: ${props.titleHighlightColor};`;
};

const renderTitle = () => {
    if (!props.titleHighlight) return props.title;

    const parts = props.title.split(props.titleHighlight);

    if (parts.length === 1) return props.title;

    const style = getHighlightStyle();

    return parts.map((part, index) => (index === 0 ? part : `<span style="${style}">${props.titleHighlight}</span>${part}`)).join('');
};

const renderSubtitle = () => {
    if (!props.subtitleHighlight) return props.subtitle;

    const parts = props.subtitle.split(props.subtitleHighlight);

    if (parts.length === 1) return props.subtitle;

    const style = getHighlightStyle();

    return parts.map((part, index) => (index === 0 ? part : `<span style="${style}">${props.subtitleHighlight}</span>${part}`)).join('');
};
</script>

<style scoped>
/* Additional styles if needed */
</style>
